import {FilterOption} from "../types/travel.ts";
import {useMemo, useCallback} from 'preact/hooks';
import {JSX} from "preact";

interface YearMonthPickerProps {
    options: FilterOption[] // Array of YYYYMM strings
    selectedValues: string[] // Array of selected YYYYMM strings
    onSelectionChange: (values: string[]) => void
    isAlleSpaeterenTermineSelected: boolean,
    setIsAlleSpaeterenTermineSelected: (value: boolean) => void
    isFlexibelSelected: boolean,
    setIsFlexibelSelected: (value: boolean) => void
    setIsOpen?: (value: boolean) => void
}

// Move static data outside component to avoid recreation
const MONTH_NAMES = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
] as const;

export function YearMonthPicker({ options,
                                    selectedValues,
                                    onSelectionChange,
                                    isAlleSpaeterenTermineSelected,
                                    setIsAlleSpaeterenTermineSelected,
                                    isFlexibelSelected,
                                    setIsFlexibelSelected,
                                    setIsOpen
                                }: YearMonthPickerProps) {

    // Memoize available months transformation to avoid recreating on every render
    const availableMonthsSet = useMemo(() =>
        new Set(options.map(o => o.value.toString())),
        [options]
    );

    // Memoize selected values set for O(1) lookups
    const selectedValuesSet = useMemo(() =>
        new Set(selectedValues),
        [selectedValues]
    );

    // Memoize current date calculation
    const currentDateInfo = useMemo(() => {
        const now = new Date();
        return {
            year: now.getFullYear(),
            month: now.getMonth() // 0-based
        };
    }, []);

    // Memoize months generation
    const months = useMemo(() => {
        const result = [];
        for (let i = 0; i < 12; i++) {
            const monthIndex = (currentDateInfo.month + i) % 12;
            const year = currentDateInfo.year + Math.floor((currentDateInfo.month + i) / 12);
            const yearMonth = `${year}${String(monthIndex + 1).padStart(2, '0')}`;
            result.push({
                yearMonth,
                label: `${MONTH_NAMES[monthIndex]}<br>${year}`,
                isAvailable: availableMonthsSet.has(yearMonth),
                isSelected: selectedValuesSet.has(yearMonth)
            });
        }
        return result;
    }, [availableMonthsSet, selectedValuesSet, currentDateInfo]);

    // Memoize event handlers to prevent unnecessary re-renders
    const handleMonthClick = useCallback((yearMonth: string, event: JSX.TargetedMouseEvent<HTMLButtonElement>) => {
        const isCurrentlySelected = selectedValuesSet.has(yearMonth);
        let newSelection: string[];

        if (isCurrentlySelected) {
            // Remove from selection
            newSelection = selectedValues.filter(value => value !== yearMonth);
            // Remove focus when deselecting
            event.currentTarget.blur();
        } else {
            // Add to selection
            newSelection = [...selectedValues, yearMonth];
        }

        newSelection = newSelection.filter(value => value !== 'later' && value !== 'flexible');

        setIsAlleSpaeterenTermineSelected(false);
        setIsFlexibelSelected(false);
        onSelectionChange(newSelection);
    }, [selectedValues, selectedValuesSet, setIsAlleSpaeterenTermineSelected, setIsFlexibelSelected, onSelectionChange]);

    const handleAllLaterTermsClick = useCallback(() => {
        // Clear all other selections and set only "later"
        if(!isAlleSpaeterenTermineSelected) {
            setIsAlleSpaeterenTermineSelected(true);
            setIsFlexibelSelected(false);
            onSelectionChange(["later"]);
        } else {
            setIsAlleSpaeterenTermineSelected(false);
            onSelectionChange([]);
        }
    }, [isAlleSpaeterenTermineSelected, setIsAlleSpaeterenTermineSelected, setIsFlexibelSelected, onSelectionChange]);

    const handleFlexibelClick = useCallback(() => {
        // Clear all other selections and set only "flexible"
        if(!isFlexibelSelected) {
            setIsFlexibelSelected(true);
            setIsAlleSpaeterenTermineSelected(false);
            onSelectionChange(["flexible"]);
        } else {
            setIsFlexibelSelected(false);
            onSelectionChange([]);
        }
    }, [isFlexibelSelected, setIsFlexibelSelected, setIsAlleSpaeterenTermineSelected, onSelectionChange]);

    const handleClearClick =  () => {
        onSelectionChange([]);
    }

    // Memoize button styles to avoid recalculation
    const getMonthButtonClass = useCallback((isAvailable: boolean, isSelected: boolean) => {
        const baseClasses = 'px-3 py-2 rounded-lg text-12px leading-[1.2em] text-center min-h-[55px] flex items-center justify-center border-2';

        if (!isAvailable) {
            return `${baseClasses} text-gray-400 border-theme-gray-light cursor-not-allowed`;
        }

        if (isSelected) {
            return `${baseClasses} bg-theme-hintergrund border-theme-black text-theme-black font-semibold`;
        }

        return `${baseClasses} 
            text-gray-900 border-theme-gray-light hover:bg-theme-hintergrund hover:border-theme-hintergrund hover:font-semibold hover:text-theme-black
            focus:bg-theme-hintergrund focus:border-theme-hintergrund focus:font-semibold focus:text-theme-black
        `;
    }, []);

    const laterButtonClass = useCallback((alleSpaeterenReisenButtonEnabled: boolean, isAlleSpaeterenTermineSelected: boolean)  => {

        const baseClasses = `w-full px-4 py-3 text-12px border-2 rounded-lg col-span-2 min-h-[55px]`;
        if (isAlleSpaeterenTermineSelected) {
            return `${baseClasses} bg-theme-hintergrund border-theme-black text-theme-black font-semibold`
        }

        if(!alleSpaeterenReisenButtonEnabled) {
            return `${baseClasses} text-gray-400 border-theme-gray-light cursor-not-allowed`;
        }

        return `${baseClasses} text-gray-600 border-theme-gray-light hover:bg-theme-hintergrund hover:border-theme-hintergrund hover:font-semibold hover:text-theme-black
                    focus:bg-theme-hintergrund focus:border-theme-hintergrund focus:font-semibold focus:text-theme-black`;
    }, [])


    const flexibleButtonClass = useMemo(() =>
        `w-full px-4 py-3 text-12px border-2 rounded-lg min-h-[55px] ${
            isFlexibelSelected
                ? 'bg-theme-hintergrund border-theme-black text-theme-black font-semibold'
                : `
                    text-gray-600 border-theme-gray-light hover:bg-theme-hintergrund hover:border-theme-hintergrund hover:font-semibold hover:text-theme-black
                    focus:bg-theme-hintergrund focus:border-theme-hintergrund focus:font-semibold focus:text-theme-black
                `
        }`,
        [isFlexibelSelected]
    );

    const alleSpaeterenReisenButtonEnabled = useMemo(() => {
        const now = new Date();
        const yearMonth = `${now.getFullYear() + 1}${String(now.getMonth() + 1).padStart(2, '0')}`;
        return Array.from(availableMonthsSet).some(value => value >= yearMonth);
    }, [availableMonthsSet]);


    return (
        <div className="year-month-picker pt-4">
            {/* Grid of months */}
            <div className="grid grid-cols-3 gap-2 mb-2">
                {months.map((month) => (
                    <button
                        key={month.yearMonth}
                        onClick={(e) => month.isAvailable && handleMonthClick(month.yearMonth, e)}
                        disabled={!month.isAvailable}
                        className={getMonthButtonClass(month.isAvailable, month.isSelected)}
                    >
                        <span
                            className={!month.isAvailable ? 'line-through text-theme-gray-light-dark' : ''}
                            dangerouslySetInnerHTML={{ __html: month.label }}
                        />
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-3 gap-2">
                <button
                    disabled={!alleSpaeterenReisenButtonEnabled}
                    onClick={handleAllLaterTermsClick}
                    className={laterButtonClass(alleSpaeterenReisenButtonEnabled, isAlleSpaeterenTermineSelected)}
                >
                    <span className={!alleSpaeterenReisenButtonEnabled ? `line-through text-theme-gray-light-dark` : ``}>Alle späteren Termine</span>
                </button>

                <button
                    onClick={handleFlexibelClick}
                    className={flexibleButtonClass}
                >
                    Flexibel
                </button>
            </div>

            <div className={`flex justify-between mt-5`}>
                <button className={`btn height-40 ghost red rounded-8px! px-5! font-montserrat! font-bold! border-2! cursor-pointer`}
                        onClick={() => {
                            handleClearClick();
                            if(setIsOpen) {
                                setIsOpen(false)
                            }
                        }}>Löschen</button>
                <button className={`btn height-40 red rounded-8px! px-5! font-montserrat! cursor-pointer`}
                        onClick={() => {
                            if(setIsOpen) {
                                setIsOpen(false)
                            }
                        }}>Weiter</button>
            </div>
        </div>
    )
}
